import { useCallback, useEffect, useState } from "react";
import {
  ShieldAlert, LogOut, RefreshCw, Image as ImageIcon, AlertCircle,
  CheckCircle2, MessageSquare, BarChart3, Database, Calendar, Mail,
  Download, Clock,
} from "lucide-react";
import { API_BASE } from "@/lib/api";

const STORAGE_KEY = "monprecieux.admin.key";

const adminFetch = async (path, key, opts = {}) => {
  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      "X-Admin-Key": key,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
};

const downloadCsv = async (path, key, fallbackName) => {
  const res = await fetch(`${API_BASE}${path}`, {
    headers: { "X-Admin-Key": key },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = disposition.match(/filename="([^"]+)"/);
  const filename = match ? match[1] : fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const CATEGORY_LABEL = {
  smartphone: "Smartphones",
  tablette: "Tablettes",
  "montre-connectee": "Montres",
};

const StatCard = ({ icon: Icon, label, value, hint, testId }) => (
  <div
    data-testid={testId}
    className="gold-frame rounded-2xl bg-[hsl(var(--bg-elev))]/70 p-5"
  >
    <div className="flex items-start justify-between mb-3">
      <Icon size={18} className="text-gold-bright" />
      <span className="text-[10px] uppercase tracking-[0.25em] text-[hsl(var(--text-dim))]">
        {label}
      </span>
    </div>
    <div className="font-display text-3xl text-[hsl(var(--text))] tabular-nums">
      {value}
    </div>
    {hint && (
      <div className="text-xs text-[hsl(var(--text-muted))] mt-2">{hint}</div>
    )}
  </div>
);

const ActionButton = ({ onClick, busy, icon: Icon, label, hint, testId }) => (
  <button
    onClick={onClick}
    disabled={busy}
    data-testid={testId}
    className="gold-frame rounded-xl bg-[hsl(var(--bg-elev))]/70 p-4 text-left disabled:opacity-60 disabled:cursor-not-allowed transition-all"
  >
    <div className="flex items-center gap-3 mb-2">
      <div className="size-10 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border-soft))] grid place-items-center text-gold-bright">
        <Icon size={18} className={busy ? "animate-spin" : ""} />
      </div>
      <div className="font-medium">{label}</div>
    </div>
    <div className="text-xs text-[hsl(var(--text-muted))] leading-relaxed">
      {hint}
    </div>
  </button>
);

const LoginForm = ({ onSubmit, error }) => {
  const [key, setKey] = useState("");
  return (
    <div className="container-mp pt-20 pb-16">
      <div className="max-w-md mx-auto">
        <div className="text-xs uppercase tracking-[0.25em] text-gold-bright mb-2">✦ Espace admin</div>
        <h1 className="font-display text-3xl md:text-4xl mb-2">Authentification</h1>
        <p className="text-[hsl(var(--text-muted))] mb-8">
          Saisissez la clé d’administration pour accéder au tableau de bord.
        </p>
        {error && (
          <div className="mb-5 p-4 rounded-xl border border-red-500/30 bg-red-500/[0.08] flex items-start gap-3">
            <AlertCircle size={18} className="text-red-300 mt-0.5 shrink-0" />
            <div className="text-sm text-red-100">{error}</div>
          </div>
        )}
        <form
          onSubmit={(e) => { e.preventDefault(); onSubmit(key.trim()); }}
          className="grid gap-4"
        >
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Clé X-Admin-Key"
            className="field"
            data-testid="admin-key-input"
            autoFocus
          />
          <button
            type="submit"
            className="btn-gold"
            disabled={!key.trim()}
            data-testid="admin-key-submit"
          >
            <ShieldAlert size={16} /> Se connecter
          </button>
        </form>
      </div>
    </div>
  );
};

const Banner = ({ tone, children, onClose }) => {
  const palette =
    tone === "success"
      ? "border-[hsl(var(--gold))]/40 bg-[hsl(var(--gold))]/[0.08] text-gold-bright"
      : "border-red-500/30 bg-red-500/[0.08] text-red-100";
  return (
    <div className={`mb-6 p-4 rounded-xl border flex items-start gap-3 ${palette}`}>
      {tone === "success" ? (
        <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
      ) : (
        <AlertCircle size={18} className="mt-0.5 shrink-0" />
      )}
      <div className="text-sm flex-1">{children}</div>
      {onClose && (
        <button onClick={onClose} className="text-xs underline">fermer</button>
      )}
    </div>
  );
};

const Dashboard = ({ adminKey, onLogout }) => {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [cron, setCron] = useState(null);
  const [busy, setBusy] = useState({ catalog: false, images: false });
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, c, q, cr] = await Promise.all([
        adminFetch("/admin/stats", adminKey),
        adminFetch("/admin/contacts?limit=30", adminKey),
        adminFetch("/admin/quotes?limit=20", adminKey),
        adminFetch("/admin/cron-status", adminKey).catch(() => null),
      ]);
      setStats(s);
      setContacts(c.items || []);
      setQuotes(q.items || []);
      setCron(cr);
    } catch (e) {
      setError(e.message);
    }
  }, [adminKey]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-poll stats while a scrape is running so the progress bar updates live.
  useEffect(() => {
    if (stats?.variant_refresh_progress?.status !== "running") return;
    const id = setInterval(() => {
      adminFetch("/admin/stats", adminKey).then(setStats).catch(() => {});
    }, 5000);
    return () => clearInterval(id);
  }, [stats?.variant_refresh_progress?.status, adminKey]);

  const triggerCatalogScrape = async () => {
    setBusy((b) => ({ ...b, catalog: true }));
    setError(null);
    try {
      await adminFetch("/admin/scrape", adminKey, { method: "POST" });
      setNotice("Scrape du catalogue lancé en arrière-plan (≈ 9-10 min, prix exacts par capacité). Rechargez les stats dans une dizaine de minutes.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy((b) => ({ ...b, catalog: false }));
    }
  };

  const triggerImageScrape = async () => {
    setBusy((b) => ({ ...b, images: true }));
    setError(null);
    try {
      await adminFetch("/admin/scrape-images", adminKey, { method: "POST" });
      setNotice("Scrape des images lancé en arrière-plan (≈ 5-10 min). Les visuels apparaîtront progressivement.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy((b) => ({ ...b, images: false }));
    }
  };

  const triggerVariantPriceRefresh = async () => {
    setBusy((b) => ({ ...b, prices: true }));
    setError(null);
    try {
      await adminFetch("/admin/refresh-variant-prices", adminKey, { method: "POST" });
      setNotice("Rafraîchissement des prix par capacité lancé (≈ 6-7 min). La barre de progression apparaît ci-dessous.");
      // Trigger stats reload so the progress bar appears immediately
      setTimeout(() => adminFetch("/admin/stats", adminKey).then(setStats).catch(() => {}), 1500);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy((b) => ({ ...b, prices: false }));
    }
  };

  const applySeedImages = async () => {
    setBusy((b) => ({ ...b, seed: true }));
    setError(null);
    try {
      const res = await adminFetch("/admin/apply-seed-images", adminKey, { method: "POST" });
      setNotice(`Visuels appliqués : ${res.applied} modèles mis à jour (sur ${res.source_entries} entrées). Rechargez les stats.`);
      await loadAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy((b) => ({ ...b, seed: false }));
    }
  };

  if (!stats && !error) {
    return (
      <div className="container-mp pt-12 pb-16">
        <div className="mp-shimmer h-12 w-72 rounded mb-6" />
        <div className="grid md:grid-cols-3 gap-4">
          {[0,1,2,3,4,5].map((i) => <div key={i} className="mp-shimmer h-32 rounded-2xl" />)}
        </div>
      </div>
    );
  }

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("fr-FR", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        timeZone: "Europe/Paris",
      });
    } catch { return iso; }
  };

  const relativeTime = (iso) => {
    if (!iso) return null;
    try {
      const diffMs = Date.now() - new Date(iso).getTime();
      const min = Math.round(diffMs / 60000);
      if (min < 1) return "à l'instant";
      if (min < 60) return `il y a ${min} min`;
      const h = Math.round(min / 60);
      if (h < 24) return `il y a ${h} h`;
      const d = Math.round(h / 24);
      return `il y a ${d} j`;
    } catch { return null; }
  };

  return (
    <div className="container-mp pt-10 pb-16">
      <div className="flex items-center justify-between mb-2 gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-gold-bright">✦ Back-office</div>
          <h1 className="font-display text-3xl md:text-4xl">Tableau de bord</h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={loadAll} className="btn-ghost text-sm" data-testid="admin-refresh">
            <RefreshCw size={14} /> Rafraîchir
          </button>
          <button onClick={onLogout} className="btn-ghost text-sm" data-testid="admin-logout">
            <LogOut size={14} /> Déconnexion
          </button>
        </div>
      </div>
      <div className="gold-divider mb-8" />

      {error && <Banner tone="error">{error}</Banner>}
      {notice && <Banner tone="success" onClose={() => setNotice(null)}>{notice}</Banner>}

      {cron && cron.enabled && cron.jobs?.length > 0 && (
        <div
          data-testid="admin-cron-status"
          className="mb-6 flex items-center gap-2 text-xs text-[hsl(var(--text-muted))]"
        >
          <Clock size={12} className="text-gold-bright" />
          <span>
            Cron scrape catalogue activé — prochaine exécution :{" "}
            <span className="text-gold-bright tabular-nums">
              {cron.jobs[0].next_run_time
                ? new Date(cron.jobs[0].next_run_time).toLocaleString("fr-FR", {
                    dateStyle: "medium",
                    timeStyle: "short",
                    timeZone: "Europe/Paris",
                  }) + " (heure de Paris)"
                : "—"}
            </span>
          </span>
        </div>
      )}

      {cron && !cron.enabled && (
        <div
          data-testid="admin-cron-disabled"
          className="mb-6 flex items-center gap-2 text-xs text-amber-300/90 bg-amber-300/[0.06] border border-amber-300/20 rounded-lg px-3 py-2"
        >
          <AlertCircle size={12} />
          <span>
            Scraping automatique <strong>désactivé</strong>.
            Les mises à jour catalogue ne se lancent que manuellement depuis cette page.
          </span>
        </div>
      )}

      {stats?.variant_refresh_progress?.status === "running" && (
        <div
          data-testid="admin-scrape-progress"
          className="mb-6 rounded-lg border border-gold-bright/30 bg-gold-bright/5 p-3"
        >
          <div className="flex items-center justify-between text-xs text-[hsl(var(--text-muted))] mb-2">
            <span className="text-gold-bright font-medium flex items-center gap-2">
              <RefreshCw size={12} className="animate-spin" />
              Rafraîchissement des prix par capacité en cours…
            </span>
            <span className="tabular-nums">
              {stats.variant_refresh_progress.done} / {stats.variant_refresh_progress.total}
              {" "}({stats.variant_refresh_progress.updated || 0} prix mis à jour)
            </span>
          </div>
          <div className="h-1.5 w-full bg-black/30 rounded-full overflow-hidden">
            <div
              className="h-full bg-gold-bright transition-all duration-500"
              style={{ width: `${Math.round(100 * (stats.variant_refresh_progress.done / (stats.variant_refresh_progress.total || 1)))}%` }}
            />
          </div>
        </div>
      )}

      {/* Stats grid */}
      <section className="grid md:grid-cols-3 lg:grid-cols-6 gap-3 mb-10">
        <StatCard testId="stat-categories" icon={Database} label="Catégories" value={stats?.categories || 0} />
        <StatCard testId="stat-brands"     icon={Database} label="Marques"   value={stats?.brands || 0} />
        <StatCard testId="stat-models"     icon={Database} label="Modèles"   value={stats?.models || 0}
                  hint={`${stats?.models_with_image || 0} avec image`} />
        <StatCard testId="stat-quotes"     icon={BarChart3} label="Devis"   value={stats?.quotes || 0} />
        <StatCard testId="stat-contacts"   icon={MessageSquare} label="Messages" value={stats?.contacts || 0} />
        <StatCard testId="stat-last-scrape" icon={Calendar}  label="Dernier scrape"
                  value={formatDate(stats?.last_scraped_at)}
                  hint={
                    stats?.variant_prices_refreshed_at
                      ? `Prix : ${relativeTime(stats.variant_prices_refreshed_at)}`
                      : relativeTime(stats?.last_scraped_at) || "Aucun scrape enregistré"
                  } />
      </section>

      {/* Per-category breakdown */}
      <section className="mb-10">
        <h2 className="font-display text-xl mb-4 tracking-wide">Couverture image par catégorie</h2>
        <div className="grid md:grid-cols-3 gap-3">
          {Object.entries(stats?.by_category || {}).map(([slug, data]) => {
            const pct = data.total ? Math.round(100 * data.with_image / data.total) : 0;
            return (
              <div key={slug} className="gold-frame rounded-xl bg-[hsl(var(--bg-elev))]/70 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-medium">{CATEGORY_LABEL[slug] || slug}</div>
                  <div className="text-xs text-gold-bright tabular-nums">{pct}%</div>
                </div>
                <div className="h-2 rounded-full bg-[hsl(var(--bg))] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[hsl(var(--gold-deep))] to-[hsl(var(--gold-bright))]"
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <div className="text-xs text-[hsl(var(--text-muted))] mt-2 tabular-nums">
                  {data.with_image} / {data.total} avec visuel
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Admin actions */}
      <section className="mb-10">
        <h2 className="font-display text-xl mb-4 tracking-wide">Actions</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          <ActionButton
            testId="admin-action-scrape"
            icon={RefreshCw}
            label="Relancer le scrape catalogue"
            hint="Re-scrape complet des catégories / marques / modèles depuis CompaRecycle, avec récupération du prix exact par capacité via les fiches produits (≈ 9-10 min)."
            onClick={triggerCatalogScrape}
            busy={busy.catalog}
          />
          <ActionButton
            testId="admin-action-refresh-prices"
            icon={RefreshCw}
            label="Rafraîchir les prix par capacité"
            hint="Rejoue uniquement la Phase B (prix par capacité) sans re-scraper le catalogue. Idéal en rattrapage si la barre s'est bloquée (≈ 6-7 min)."
            onClick={triggerVariantPriceRefresh}
            busy={busy.prices}
          />
          <ActionButton
            testId="admin-action-seed-images"
            icon={ImageIcon}
            label="Appliquer les visuels (seed)"
            hint="Applique instantanément les 2890 visuels pré-scrapés livrés avec le code. À privilégier sur Starter (pas de Playwright requis)."
            onClick={applySeedImages}
            busy={busy.seed}
          />
          <ActionButton
            testId="admin-action-images"
            icon={ImageIcon}
            label="Re-scrape live des images"
            hint="Headless Playwright (≈ 20-30 min sur Starter). Nécessite chromium installé sur le pod. À éviter sauf en cas de catalogue très récent."
            onClick={triggerImageScrape}
            busy={busy.images}
          />
        </div>
      </section>

      {/* Recent quotes */}
      <section className="mb-10">
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-display text-xl tracking-wide">Derniers devis ({quotes.length})</h2>
          <button
            data-testid="admin-export-quotes-csv"
            onClick={() =>
              downloadCsv("/admin/quotes.csv", adminKey, "monprecieux-devis.csv")
                .catch((e) => setError(e.message))
            }
            className="btn-ghost text-xs"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
        <div className="overflow-x-auto gold-frame rounded-2xl bg-[hsl(var(--bg-elev))]/40">
          <table className="w-full text-sm" data-testid="admin-quotes-table">
            <thead className="text-[hsl(var(--text-muted))] text-xs uppercase tracking-wider">
              <tr className="border-b border-[hsl(var(--border-soft))]/60">
                <th className="text-left p-3 font-normal">Date</th>
                <th className="text-left p-3 font-normal">Modèle</th>
                <th className="text-left p-3 font-normal">Marque</th>
                <th className="text-right p-3 font-normal">Prix max</th>
                <th className="text-right p-3 font-normal">Estimation</th>
              </tr>
            </thead>
            <tbody>
              {quotes.length === 0 ? (
                <tr><td colSpan={5} className="text-center text-[hsl(var(--text-muted))] p-8">Aucun devis pour l’instant.</td></tr>
              ) : quotes.map((q, i) => (
                <tr key={q.created_at || i} className="border-b border-[hsl(var(--border-soft))]/30 last:border-0">
                  <td className="p-3 text-[hsl(var(--text-muted))]">{formatDate(q.created_at)}</td>
                  <td className="p-3">{q.model?.name}</td>
                  <td className="p-3 text-[hsl(var(--text-muted))]">{q.model?.brand_label}</td>
                  <td className="p-3 text-right tabular-nums text-[hsl(var(--text-muted))]">{q.result?.max_price?.toLocaleString("fr-FR") || "—"} €</td>
                  <td className="p-3 text-right tabular-nums text-gold-bright">{q.result?.estimated_price?.toLocaleString("fr-FR") || "—"} €</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Contact messages */}
      <section>
        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 className="font-display text-xl tracking-wide">Messages reçus ({contacts.length})</h2>
          <button
            data-testid="admin-export-contacts-csv"
            onClick={() =>
              downloadCsv("/admin/contacts.csv", adminKey, "monprecieux-contacts.csv")
                .catch((e) => setError(e.message))
            }
            className="btn-ghost text-xs"
          >
            <Download size={14} /> Export CSV
          </button>
        </div>
        <div className="grid gap-3" data-testid="admin-contacts-list">
          {contacts.length === 0 ? (
            <div className="text-center text-[hsl(var(--text-muted))] p-8 gold-frame rounded-2xl">
              Aucun message pour l’instant.
            </div>
          ) : contacts.map((c, i) => (
            <div key={c.created_at || i} className="gold-frame rounded-2xl bg-[hsl(var(--bg-elev))]/40 p-5">
              <div className="flex items-start justify-between gap-3 mb-2">
                <div>
                  <div className="font-medium">{c.name}</div>
                  <a href={`mailto:${c.email}`} className="text-xs text-gold-bright/85 inline-flex items-center gap-1 hover:underline">
                    <Mail size={12} /> {c.email}
                  </a>
                </div>
                <div className="text-xs text-[hsl(var(--text-muted))] shrink-0">{formatDate(c.created_at)}</div>
              </div>
              <div className="text-sm font-medium mb-2">{c.subject}</div>
              <div className="text-sm text-[hsl(var(--text-muted))] whitespace-pre-line leading-relaxed">
                {c.message}
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default function Admin() {
  const [adminKey, setAdminKey] = useState(() => {
    try { return sessionStorage.getItem(STORAGE_KEY) || ""; } catch { return ""; }
  });
  const [loginError, setLoginError] = useState(null);

  const handleLogin = async (key) => {
    setLoginError(null);
    try {
      await adminFetch("/admin/stats", key);
      try {
        sessionStorage.setItem(STORAGE_KEY, key);
      } catch (e) {
        console.warn("Could not persist admin session to sessionStorage:", e);
      }
      setAdminKey(key);
    } catch (e) {
      setLoginError("Clé invalide ou serveur indisponible.");
    }
  };

  const handleLogout = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn("Could not clear admin session from sessionStorage:", e);
    }
    setAdminKey("");
  };

  if (!adminKey) return <LoginForm onSubmit={handleLogin} error={loginError} />;
  return <Dashboard adminKey={adminKey} onLogout={handleLogout} />;
}
